import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";
import { toast } from "react-toastify";
import AOS from "aos";
import "aos/dist/aos.css";
import { FaArrowLeft, FaSearch, FaDownload, FaFilter, FaTimes, FaCheck } from "react-icons/fa";

function DefaulterList() {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [studentsWithoutProjects, setStudentsWithoutProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterDepartment, setFilterDepartment] = useState("");
    const [filterYear, setFilterYear] = useState("");
    const [departments, setDepartments] = useState([]);
    const [years, setYears] = useState([]);

    useEffect(() => {
        if (!isAuthenticated) {
            toast.error("You must be logged in to view this page");
            navigate("/");
            return;
        }

        fetchStudentsWithoutProjects();
        AOS.init({ duration: 1000 });
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, []);

    const fetchStudentsWithoutProjects = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${API_BASE_URL}/api/projects/students/without-projects`
            );

            if (response.status === 200 && response.data.success) {
                const data = response.data.data || [];
                setStudentsWithoutProjects(data);

                // Extract unique departments and years for filters
                const uniqueDepartments = [
                    ...new Set(
                        data
                            .map((student) => student.department)
                            .filter(Boolean)
                    ),
                ];
                const uniqueYears = [
                    ...new Set(
                        data.map((student) => student.year).filter(Boolean)
                    ),
                ];

                setDepartments(uniqueDepartments);
                setYears(uniqueYears);
            } else {
                throw new Error("Failed to fetch students without projects");
            }
        } catch (error) {
            console.error("Error fetching students without projects:", error);
            console.error("Response data:", error.response?.data);
            console.error("Status:", error.response?.status);
            toast.error("Failed to load students data");

            // If unauthorized, redirect to login
            if (error.response?.status === 401) {
                toast.error("Your session has expired. Please login again");
                navigate("/");
            }
        } finally {
            setLoading(false);
        }
    };

    const goBack = () => {
        navigate("/admin/home");
    };

    const exportToCSV = () => {
        if (filteredStudents.length === 0) return;

        const headers = ["Roll No", "Name", "Email", "Department", "Year"];

        const csvRows = [
            headers.join(","),
            ...filteredStudents.map((student) =>
                [
                    student.rollNo,
                    `"${student.name}"`,
                    student.email,
                    student.department,
                    student.year,
                ].join(",")
            ),
        ];

        const csvContent = csvRows.join("\n");
        const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute(
            "download",
            `students_without_projects_${new Date()
                .toISOString()
                .slice(0, 10)}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success("CSV file downloaded successfully");
    };

    // Filter students based on search term and filters
    const filteredStudents = studentsWithoutProjects.filter((student) => {
        const matchesSearch =
            student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            student.rollNo?.toString().includes(searchTerm);

        const matchesDepartment =
            filterDepartment === "" || student.department === filterDepartment;
        const matchesYear = filterYear === "" || student.year === filterYear;

        return matchesSearch && matchesDepartment && matchesYear;
    });

    const clearFilters = () => {
        setSearchTerm("");
        setFilterDepartment("");
        setFilterYear("");
    };

    return (
        <div className="min-h-screen p-6 md:p-8 font-sans">
            <div className="max-w-7xl mx-auto">
                <div
                    className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 pb-4 border-b border-gray-200 gap-4"
                    data-aos="fade-down"
                >
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Students Without Project Submissions</h1>
                        <p className="mt-1 text-gray-500 font-medium">
                            The following students have not submitted any projects yet
                        </p>
                    </div>
                    <button
                        className="bg-[#B7202E] text-white px-5 py-2.5 rounded-lg font-medium hover:bg-[#961925] transition-all flex items-center gap-2 shadow-sm hover:translate-y-px"
                        onClick={goBack}
                    >
                        <FaArrowLeft /> Back to Dashboard
                    </button>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm mb-6 border border-gray-100" data-aos="fade-up">
                    <div className="relative mb-6">
                        <input
                            type="text"
                            className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B7202E]/20 focus:border-[#B7202E] transition-all text-gray-700"
                            placeholder="Search by name, email or roll number..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>

                    <div className="flex flex-wrap gap-4 items-end">
                        <div className="flex flex-col gap-1 w-full md:w-auto flex-grow md:flex-grow-0">
                            <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                                <FaFilter className="text-xs text-gray-400" /> Department:
                            </label>
                            <select
                                className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#B7202E]/20 focus:border-[#B7202E] min-w-[200px] text-gray-700 cursor-pointer"
                                value={filterDepartment}
                                onChange={(e) => setFilterDepartment(e.target.value)}
                            >
                                <option value="">All Departments</option>
                                {departments.map((dept) => (
                                    <option key={dept} value={dept}>
                                        {dept}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col gap-1 w-full md:w-auto flex-grow md:flex-grow-0">
                            <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                                <FaFilter className="text-xs text-gray-400" /> Year:
                            </label>
                            <select
                                className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#B7202E]/20 focus:border-[#B7202E] min-w-[150px] text-gray-700 cursor-pointer"
                                value={filterYear}
                                onChange={(e) => setFilterYear(e.target.value)}
                            >
                                <option value="">All Years</option>
                                {years.map((year) => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <button
                            className="flex items-center gap-2 bg-gray-100 text-gray-600 px-5 py-2.5 rounded-lg font-medium hover:bg-gray-200 transition-colors md:ml-auto"
                            onClick={clearFilters}
                        >
                            <FaTimes /> Clear Filters
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-aos="fade-up">
                    <div className="p-6 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50 border-b border-gray-100">
                        <div className="text-gray-600 font-medium">
                            <span className="text-[#B7202E] font-bold text-lg">{filteredStudents.length}</span> results found
                        </div>
                        <button
                            className="bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed hover:shadow-md"
                            onClick={exportToCSV}
                            disabled={filteredStudents.length === 0}
                        >
                            <FaDownload /> Export to CSV
                        </button>
                    </div>

                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="w-12 h-12 border-4 border-gray-200 border-t-[#B7202E] rounded-full animate-spin mb-4"></div>
                            <p className="text-gray-500 font-medium">Loading student data...</p>
                        </div>
                    ) : filteredStudents.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 text-gray-700 border-b border-gray-200">
                                        <th className="p-4 font-semibold text-sm whitespace-nowrap">Roll No</th>
                                        <th className="p-4 font-semibold text-sm whitespace-nowrap">Name</th>
                                        <th className="p-4 font-semibold text-sm whitespace-nowrap">Email</th>
                                        <th className="p-4 font-semibold text-sm whitespace-nowrap">Department</th>
                                        <th className="p-4 font-semibold text-sm whitespace-nowrap">Year</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredStudents.map((student) => (
                                        <tr key={student._id} className="hover:bg-red-50/20 transition-colors">
                                            <td className="p-4 text-sm font-medium text-gray-800">{student.rollNo}</td>
                                            <td className="p-4 text-sm font-semibold text-[#B7202E]">{student.name}</td>
                                            <td className="p-4 text-sm text-gray-600">{student.email}</td>
                                            <td className="p-4 text-sm text-gray-600">
                                                <span className="bg-gray-100 px-2 py-1 rounded text-xs font-medium text-gray-700 border border-gray-200">
                                                    {student.department}
                                                </span>
                                            </td>
                                            <td className="p-4 text-sm text-gray-600">{student.year}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-16 px-4">
                            {searchTerm || filterDepartment || filterYear ? (
                                <div className="text-gray-500">
                                    <FaSearch className="mx-auto text-4xl mb-3 text-gray-300" />
                                    <p className="text-lg font-medium text-gray-600">No students match your search criteria</p>
                                    <p className="text-sm mt-1">Try adjusting your filters or search term</p>
                                </div>
                            ) : (
                                <div className="text-green-600">
                                    <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <FaCheck className="text-3xl" />
                                    </div>
                                    <p className="text-lg font-bold">Great news!</p>
                                    <p className="font-medium mt-1">All students have submitted at least one project!</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DefaulterList;
